import { Box, Tab, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { orderService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { DbOrderManualTaskPlanningInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-infos";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { UserHasAcl } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import KanbanTaskCard from "../../Cards/KanbanTaskCard/kanban-task-card";
import ResourceNotification from "../../Notification/resource-notification";
import OrderItemTabDetails from "../OrderItemPanel/order-item-panel-tab-details";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import PanelTabs from "../PanelTabs";
import { useTranslation } from "react-i18next";

export interface UserTaskPlanningPanelProps {
  width?: number;
  open: boolean;
  task: DbOrderManualTaskPlanningInfos | undefined;
  users: DbUserBasicInfos[];
  resources: DbOrderManualTaskPlanningResourceInfos[];
  onClose?: React.MouseEventHandler;
}

const UserTaskPlanningPanel: React.FC<UserTaskPlanningPanelProps> = (props) => {
  const { t } = useTranslation();
  const { task, users, resources } = props;
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [orderItem, setOrderItem] = useState<DbOrderItemInfos | null>(null);

  const enqueueSnackbar = useCustomSnackbar();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (task && task.orderItemId)
      orderService.GetOrderItem(task.orderItemId).then((response) => {
        if (!response.success)
          enqueueSnackbar(
            t("alert:error.unableToFetchOrderItem", { orderItemId: task.orderItemId, message: response.message }),
            "error"
          );
        else setOrderItem(response.item);
      });
  }, [task]);

  return (
    <Panel
      header={
        <PanelHeader title={t("panel:title.userTaskDetails")} onClose={props.onClose as PanelHeaderProps["onClose"]} />
      }
      open={props.open}
      onClose={props.onClose}
    >
      {task && <KanbanTaskCard task={task} users={users} resources={resources} />}
      <PanelTabs value={selectedTab} onChange={handleChange} aria-label="basic tabs example" variant="fullWidth">
        <Tab label={t("common:orderDetails")} {...a11yProps(0)} />
        <Tab label={t("common:activities")} {...a11yProps(1)} />
        <Tab label={t("common:actions")} {...a11yProps(2)} />
      </PanelTabs>
      <Box mt={2}>
        <PanelTabContent id="ut-panel-order" activePanelIndex={selectedTab} index={0}>
          {orderItem ? (
            <OrderItemTabDetails item={orderItem} />
          ) : (
            <Typography variant="body1">{t("panel:body.noOrderItem")}</Typography>
          )}
        </PanelTabContent>
        <PanelTabContent id="ut-panel-notif" activePanelIndex={selectedTab} index={1}>
          <ResourceNotification
            resourceKind={ActivityKind.UserTask}
            resourceId={task?.id}
            enableComments={UserHasAcl(AclTypes.aclActivityAdd)}
          />
        </PanelTabContent>
        <PanelTabContent id="ut-panel-actions" activePanelIndex={selectedTab} index={2}>
          <Typography>{t("panel:userTaskPlanningPanel.body.noActionNeeded")}</Typography>
        </PanelTabContent>
      </Box>
    </Panel>
  );
};

function a11yProps(index: number) {
  return {
    id: `ut-panel-tab-${index}`,
    "aria-controls": `ut-panel-tabpanel-${index}`,
  };
}

export default UserTaskPlanningPanel;
